import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "app/store";
import { useTeamNameSelector } from "pages/team_settings/hooks";
import { Team } from "models/schemas";
import { TEAM_NAME_MAP } from "utils/gaia-constants";
import { GaiaConstants } from "utils";

/**
 * Returns the normalized allTeams array from the Redux store.
 * Handles the inconsistency where allTeams may be Team[] or { teams: Team[] }.
 */
export const useAllTeams = (): Team[] => {
  return useAppSelector((state) => {
    const teams = state.teamSettings?.allTeams as any;
    if (Array.isArray(teams)) return teams;
    if (teams?.teams && Array.isArray(teams.teams)) return teams.teams;
    return [];
  });
};

/**
 * Returns child teams for a given parent team name.
 */
export const useChildTeams = (parentTeamName: string | undefined) => {
  const allTeams = useAllTeams();

  const childTeams = useMemo(() => {
    if (!parentTeamName) return [];
    const parentTeam = allTeams.find(
      (team: Team) => team.teamId === parentTeamName,
    );
    const subTeamIds = (parentTeam as any)?.subTeams as string[] | undefined;
    if (!subTeamIds || subTeamIds.length === 0) return [];
    return allTeams.filter(
      (team: Team) =>
        team.parentTeamResourceId === parentTeamName &&
        subTeamIds.includes(team.teamId),
    );
  }, [allTeams, parentTeamName]);

  const hasSubTeams = childTeams.length > 0;

  return { childTeams, hasSubTeams };
};

/**
 * Returns a function to resolve a team's display description from its ID.
 */
export const useTeamDescription = () => {
  const allTeams = useAllTeams();

  const getTeamDescription = (teamId: string) => {
    const team = allTeams.find((t: Team) => t.teamId === teamId);
    return team?.teamDescription || TEAM_NAME_MAP[teamId] || teamId;
  };

  // Human-readable name only; undefined when the id has no description/known name (so callers never surface a raw team id).
  const getTeamDisplayName = (teamId: string): string | undefined => {
    const team = allTeams.find((t: Team) => t.teamId === teamId);
    return team?.teamDescription || TEAM_NAME_MAP[teamId] || undefined;
  };

  return { getTeamDescription, getTeamDisplayName };
};

/**
 * Checks if a team has sub-teams defined in the API response data.
 * Unlike useChildTeams (which reads from Redux and may not be populated yet),
 * this checks the subTeams property directly on the team object from the /teams API.
 * Use in contexts like the Header where you need the answer before Redux is ready.
 */
export const teamHasSubTeams = (
  teams: Team[] | undefined,
  teamId: string | undefined,
): boolean => {
  if (!teams || !teamId) return false;
  const team = teams.find((t: Team) => t.teamId === teamId);
  return !!(team as any)?.subTeams?.length;
};

/**
 * Returns the effective team name for API calls.
 * Uses the selected sub-team from localStorage if available,
 * otherwise falls back to the current team from Redux.
 */
export const useEffectiveTeamName = (): string => {
  const { teamName } = useTeamNameSelector();
  return localStorage.getItem(GaiaConstants.SELECTED_SUB_TEAM_NAME) || teamName;
};

/**
 * Hook to manage sub-team selection for forms.
 * If the current team has sub-teams, provides the list and selection state.
 * Returns the effective team name to use for API calls.
 */
export const useSubTeams = () => {
  const { teamName } = useTeamNameSelector();
  const allTeams = useAllTeams();

  // Resolve the parent team: if teamName is itself a sub-team, use its parent
  const parentTeamName = useMemo(() => {
    if (!Array.isArray(allTeams) || !teamName) return teamName;
    const currentTeam = allTeams.find((team: Team) => team.teamId === teamName);
    return currentTeam?.parentTeamResourceId || teamName;
  }, [allTeams, teamName]);

  const { childTeams: subTeams, hasSubTeams } = useChildTeams(parentTeamName);

  const [selectedSubTeam, setSelectedSubTeam] = useState<string>("");
  const [subTeamError, setSubTeamError] = useState<string>("");

  // Auto-select first sub-team only when there is exactly one option
  // Clear localStorage when team has no sub-teams
  useEffect(() => {
    if (subTeams.length === 1 && !selectedSubTeam) {
      setSelectedSubTeam(subTeams[0].teamId);
      localStorage.setItem(
        GaiaConstants.SELECTED_SUB_TEAM_NAME,
        subTeams[0].teamId,
      );
    } else if (subTeams.length > 1) {
      setSelectedSubTeam("");
      localStorage.removeItem(GaiaConstants.SELECTED_SUB_TEAM_NAME);
    } else if (!hasSubTeams) {
      setSelectedSubTeam("");
      localStorage.removeItem(GaiaConstants.SELECTED_SUB_TEAM_NAME);
    }
  }, [subTeams, hasSubTeams]);

  const effectiveTeamName = hasSubTeams ? selectedSubTeam : teamName;

  const validateSubTeam = (): boolean => {
    if (hasSubTeams && !selectedSubTeam) {
      setSubTeamError("Access group is required");
      return false;
    }
    return true;
  };

  const handleSubTeamChange = (value: string) => {
    setSelectedSubTeam(value);
    setSubTeamError("");
    localStorage.setItem(GaiaConstants.SELECTED_SUB_TEAM_NAME, value);
  };

  return {
    teamName,
    subTeams,
    hasSubTeams,
    selectedSubTeam,
    subTeamError,
    effectiveTeamName,
    validateSubTeam,
    handleSubTeamChange,
  };
};
